/**
 * BarChart - Discrete value comparison
 *
 * Usage:
 *   <BarChart
 *     title="Commits per day"
 *     data={{
 *       items: [
 *         { label: 'Mon', value: 12 },
 *         { label: 'Tue', value: 8 },
 *         { label: 'Wed', value: 15 },
 *       ]
 *     }}
 *   />
 */

import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import type { BlockProps, BarChartData } from './types';
import { CHART_COLORS, getSeriesColor } from './colors';
import './blocks.css';

export interface BarChartProps extends BlockProps<BarChartData> {
  /** Chart height in pixels */
  height?: number;
  /** Horizontal bar chart */
  horizontal?: boolean;
  /** Show value labels on bars */
  showLabels?: boolean;
  /** Use single color for all bars */
  singleColor?: boolean;
}

export function BarChart({
  data,
  title,
  config,
  height = 250,
  horizontal = false,
  showLabels = false,
  singleColor = true,
  className,
}: BarChartProps) {
  const { items } = data;

  if (!items || items.length === 0) {
    return (
      <div className={`block-chart ${className || ''}`}>
        {title && <div className="block-chart-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const defaultColor = config?.colors?.[0] || CHART_COLORS[0];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;

    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: '6px',
        padding: '8px 12px',
        boxShadow: 'var(--popover-shadow, 0 2px 4px rgba(0,0,0,0.1))',
      }}>
        <p style={{
          color: 'var(--color-text)',
          fontWeight: 600,
          margin: 0,
          fontSize: '14px',
        }}>
          {label}: {payload[0].value.toLocaleString()}
        </p>
      </div>
    );
  };

  return (
    <div className={`block-chart ${className || ''}`}>
      {title && <div className="block-chart-title">{title}</div>}

      <div className="block-chart-container">
        <ResponsiveContainer width="100%" height={height}>
          <RechartsBarChart
            data={items}
            layout={horizontal ? 'vertical' : 'horizontal'}
            margin={{ top: 5, right: 20, bottom: 5, left: horizontal ? 60 : 0 }}
          >
            <CartesianGrid
              strokeDasharray="0"
              stroke="var(--color-border)"
              strokeWidth={1}
              horizontal={!horizontal}
              vertical={horizontal}
            />

            {horizontal ? (
              <>
                <XAxis
                  type="number"
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                  width={50}
                />
              </>
            ) : (
              <>
                <XAxis
                  dataKey="label"
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                />
                <YAxis
                  stroke="var(--color-text-faint)"
                  tick={{ fill: 'var(--color-text-faint)', fontSize: 10 }}
                  axisLine={{ stroke: 'var(--color-border-strong)', strokeWidth: 1 }}
                  tickLine={false}
                  tickFormatter={(value) => value.toLocaleString()}
                />
              </>
            )}

            <Tooltip content={<CustomTooltip />} />

            <Bar
              dataKey="value"
              radius={[4, 4, 0, 0]}
              maxBarSize={60}
              label={showLabels ? {
                position: 'top',
                fill: 'var(--color-text-subtle)',
                fontSize: 11,
              } : undefined}
            >
              {items.map((item, index) => (
                <Cell
                  key={index}
                  fill={item.color || (singleColor ? defaultColor : getSeriesColor(index))}
                />
              ))}
            </Bar>
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default BarChart;
